import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { taskPath } from '@/utils/taskPath'
import InboxIcon from '../../../../../assets/icons/icon-inbox.svg?react'
import SidebarUiButton from '../SidebarUiButton/SidebarUiButton.tsx'
import Tooltip from '@/components/ui/Tooltip/Tooltip'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import { useAppSelector, useAppDispatch } from '../../../../../store/hooks'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext'
import { selectOpenDropdownId } from '../../../../../store/ui/uiSelectors'
import { setOpenDropdown, setMobileSidebarOpen } from '../../../../../store/ui/uiSlice'
import { selectHasUnwatched, selectAllNotifications } from '../../../../../store/notifications/notificationsSelectors'
import { markNotificationWatchedSaga } from '../../../../../store/notifications/notificationsSlice'
import type { Notification } from '../../../../../store/notifications/notificationsSlice'
import { selectTasksById, selectTasksInitialized, selectTasksCompletedInitialized } from '../../../../../store/tasks/tasksSelectors'
import { selectProjectsById, selectProjectsLoading } from '../../../../../store/projects/projectsSelectors'
import { showToast } from '@/components/app/Toast/showToast'
import InboxDropdown from './components/InboxDropdown/InboxDropdown'
import useIsMobile from '../../../../../hooks/useIsMobile'
import css from './SidebarInboxButton.module.css'

const DROPDOWN_ID = 'sidebar-inbox-button'

const SidebarInboxButton = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { workspace } = useWorkspace()
    const isMobile = useIsMobile()
    const dot = useAppSelector(selectHasUnwatched)
    const notifications = useAppSelector(selectAllNotifications)
    const tasksById = useAppSelector(selectTasksById)
    const tasksInitialized = useAppSelector(selectTasksInitialized)
    const tasksCompletedInitialized = useAppSelector(selectTasksCompletedInitialized)
    const projectsById = useAppSelector(selectProjectsById)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const isActive = workspace && location.pathname === `/${workspace.workspaceUrlId}/inbox`
    const tasksLoaded = tasksInitialized && tasksCompletedInitialized

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
        }
    }, [openDropdownId, isDropdownOpen])

    const handleClick = () => {
        if (isMobile) {
            if (!workspace) return
            navigate(`/${workspace.workspaceUrlId}/inbox`)
            dispatch(setMobileSidebarOpen(false))
            return
        }
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleClose = () => {
        dispatch(setOpenDropdown(null))
    }

    // A notification whose task/project is gone can't be opened, and opening the task is
    // what normally marks it read — so mark it here instead of dead-ending on the click.
    const clearOrphanNotification = (n: Notification, text: string) => {
        dispatch(markNotificationWatchedSaga(n.id))
        showToast(text)
    }

    const handleNotificationClick = (n: Notification) => {
        if (!workspace) return
        if (n.taskId) {
            const task = tasksById[n.taskId]
            if (!task) {
                if (tasksLoaded) clearOrphanNotification(n, 'This task no longer exists.')
                handleClose()
                return
            }
            const path = taskPath({ workspaceUrlId: workspace.workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: task.number, pending: task.pending })
            if (!path) return
            navigate(path, { state: { backgroundLocation: location } })
        } else if (n.projectId) {
            if (!projectsById[n.projectId]) {
                if (!projectsLoading) clearOrphanNotification(n, 'This project no longer exists.')
                handleClose()
                return
            }
            navigate(`/${workspace.workspaceUrlId}/projects/${n.projectId}`)
        }
        handleClose()
    }

    const handleAllNotifications = () => {
        if (!workspace) return
        navigate(`/${workspace.workspaceUrlId}/inbox`)
        handleClose()
    }

    return (
        <DropdownWrapper>
            <div className={css.wrapper}>
                <Tooltip title="Inbox">
                    <SidebarUiButton icon={<InboxIcon />} title="Inbox" isActive={!!isActive} onClick={handleClick} />
                </Tooltip>
                {dot && <span className={css.dot} />}
            </div>
            <InboxDropdown
                isOpen={isDropdownOpen}
                onClose={handleClose}
                notifications={notifications}
                tasksById={tasksById}
                projectsById={projectsById}
                tasksLoaded={tasksLoaded}
                onNotificationClick={handleNotificationClick}
                onAllNotifications={handleAllNotifications}
            />
        </DropdownWrapper>
    )
}

export default SidebarInboxButton
